// @flow

import React, { Component, type Element, Fragment } from "react";
import { noop, times, get, isEqual, round, startCase } from "lodash";
import { Link } from "react-router-dom";
import type {
  HelpTypeEnum,
  SimpleBettingInterest
} from "@fdr/types/ProgramPage";
import type { FullResultRunner } from "@fdr/types/PastPerformance";
import type {
  RaceTypeCodeEnum,
  RacePanelLink,
  RaceCardRunner,
  RaceCardBettingInterest,
  RaceProgram,
  PastPerformanceRace,
  RunnerTagsModalProps
} from "@tvg/types/Race";
import type {
  NullaryFn,
  BinaryFn,
  TernaryFn,
  UnaryFn,
  QuadFn
} from "@tvg/types/Functional";
import Icon from "@fdr/static-ui/Icons";
import { formatSequencial } from "@tvg/formatter";
import {
  arrowDown,
  numberFire,
  arrowUp,
  caretUp,
  caretDown
} from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import type { WagerGroupEnum } from "@fdr/types/WagerTypes";
import { RacingAndSportsIcon } from "@fdr/static-ui/SvgImages/RacingAndSports";
import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import RunnerTags from "../RunnerTags";
import RunnerNumber from "../../_atoms/RunnerNumber";
import SelectionButton from "../../_atoms/SelectionButton";
import ExpandableRow from "../../_templates/ExpandableRow";
import {
  RunnerWrapper,
  RunnerSelectionWrapped,
  AnyButton,
  JockeySilk,
  JockeySilkContainer,
  RunnerJockeyWrapper,
  BetSection,
  RunnerInfoContainer,
  RunnerContainer,
  HorseDescription,
  CoupledTagContainer,
  DriverDescription,
  OddsContainer,
  OddInfo,
  ExoticBetting,
  RunnerLineWrapper,
  NumberFire,
  NumberFirePick,
  NumberFireComment,
  RunnerInfo,
  HorseName,
  ScratchedTag,
  GroupWrapper,
  HorseNameOnboardingTooltip,
  USOdds,
  USOddsContainer,
  USOddsText,
  ExpandIconWrapper
} from "./styled-components";
import Tag from "../../_atoms/Tag";
import Silk from "./silk";

type Props = {
  raceTypeCode: RaceTypeCodeEnum,
  selections: boolean[],
  isWagerable: boolean,
  numColumns: number,
  onRunnerSelect: TernaryFn<string, number, boolean, mixed>,
  redirectToRaceCard: boolean,
  raceUrl: string,
  onRunnerSelection: QuadFn<
    RacePanelLink,
    number,
    string,
    ?SyntheticKeyboardEvent<*>,
    void
  >,
  race: ?RacePanelLink,
  hasHover: boolean,
  onSelectAllRunnerEntries: UnaryFn<string, void>,
  runner: ?RaceCardRunner | FullResultRunner,
  bettingInterest: RaceCardBettingInterest | ?SimpleBettingInterest,
  isLeg: boolean,
  leg: number,
  isLogged: boolean,
  isCoupled: boolean,
  expandedInfo: TernaryFn<number, number, boolean, Element<*> | void>,
  updatesRL: boolean,
  changeUpdate: TernaryFn<string, number, boolean, void>,
  isOpen: boolean,
  openHelpModal: UnaryFn<HelpTypeEnum, void>,
  isNumberFireSection: boolean,
  currentRace: ?RaceProgram | PastPerformanceRace,
  runners: RaceCardBettingInterest[],
  isHighlighted: boolean,
  hasMarginLeft: boolean,
  hasDashedBorder: boolean,
  betAmount: number,
  freePickSource: string,
  fullWidth: boolean,
  group: ?WagerGroupEnum,
  /*
   * If is an Sky Racing World race
   */
  isSRW: boolean,
  /*
   * If it is Racing and Sports race
   */
  isRAS: boolean,
  showSilks: boolean,
  internationalLabel: string,
  index?: number,
  shouldShowFirstOnboarding: boolean,
  onTooltipClick: NullaryFn<void>,
  setOnboardingStatus: BinaryFn<string, string, void>,
  useGraphStatusV2?: boolean,
  showRunnerUpdate: boolean,
  enableNewRacePage: boolean,
  isExpandable: boolean,
  wagerTypeCode?: string,
  onRaceTagsInfoClick: UnaryFn<RunnerTagsModalProps, void>,
  selectAllButtonLabel?: string,
  handleError?: UnaryFn<number, void>
};

type State = {
  hasSilksError: boolean,
  isSilkLoaded: boolean
};

// TODO: Remove this logic from atomic
export const formatOdd = (numerator: string, denominator: string): string => {
  return !denominator ? `${numerator}/1` : `${numerator || 0}/${denominator}`;
};

// TODO: Remove this logic from atomic
const USformatOdd = (numerator: string, denominator: string): string => {
  const resultOdd = Number(numerator) / Number(denominator);

  if (!denominator) {
    return `+${Number(numerator) * 100}`;
  }
  if (Number(numerator) < Number(denominator)) {
    return `${round(-100 / resultOdd)}`;
  }
  return `+${round(resultOdd * 100)}`;
};

const noopFunction = () => {};
export default class RunnerLine extends Component<Props, State> {
  static defaultProps = {
    raceTypeCode: "T",
    isOpen: false,
    selections: [false],
    isWagerable: true,
    onRunnerSelect: noop,
    numColumns: 1,
    redirectToRaceCard: false,
    raceUrl: "/",
    onRunnerSelection: noop,
    race: null,
    hasHover: false,
    onSelectAllRunnerEntries: noop,
    runner: null,
    bettingInterest: null,
    isLeg: false,
    leg: -1,
    isLogged: false,
    isCoupled: false,
    expandedInfo: noop,
    updatesRL: false,
    changeUpdate: noop,
    openHelpModal: noop,
    currentRace: null,
    runners: [],
    isNumberFireSection: false,
    isHighlighted: false,
    hasMarginLeft: true,
    hasDashedBorder: false,
    betAmount: 0,
    isSRW: false,
    freePickSource: "handicapping",
    group: null,
    fullWidth: false,
    showSilks: false,
    internationalLabel: "",
    index: -1,
    shouldShowFirstOnboarding: false,
    onTooltipClick: noop,
    setOnboardingStatus: noop,
    useGraphStatusV2: false,
    showRunnerUpdate: false,
    isRAS: false,
    enableNewRacePage: false,
    isExpandable: true,
    selectAllButtonLabel: "Box",
    handleError: noopFunction
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      hasSilksError: false,
      isSilkLoaded: false
    };
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      this.props.betAmount !== nextProps.betAmount ||
      this.props.isOpen !== nextProps.isOpen ||
      this.props.numColumns !== nextProps.numColumns ||
      this.props.isLogged !== nextProps.isLogged ||
      this.props.hasDashedBorder !== nextProps.hasDashedBorder ||
      this.props.group !== nextProps.group ||
      this.props.showSilks !== nextProps.showSilks ||
      this.props.shouldShowFirstOnboarding !==
        nextProps.shouldShowFirstOnboarding ||
      !isEqual(this.props.runners, nextProps.runners) ||
      !isEqual(this.props.bettingInterest, nextProps.bettingInterest) ||
      !isEqual(this.props.selections, nextProps.selections) ||
      !isEqual(this.props.currentRace, nextProps.currentRace) ||
      this.props.wagerTypeCode !== nextProps.wagerTypeCode ||
      this.state.hasSilksError !== nextState.hasSilksError ||
      this.state.isSilkLoaded !== nextState.isSilkLoaded ||
      this.props.handleError !== nextProps.handleError
    );
  }

  onExpand = () => {
    if (this.props.isNumberFireSection || !this.props.isExpandable) {
      return;
    }
    this.props.changeUpdate(
      get(this.props, "runner.runnerId"),
      this.props.leg,
      this.props.isOpen
    );
  };

  getInternationalLabel = (column: number, columnsLength: number) => {
    const { group, internationalLabel } = this.props;
    const country = get(
      this.props,
      "currentRace.track.trackLocation.country",
      ""
    );
    const columnAllowed = [2];
    const maxColumnLengthAllowed = [2];
    const isNational = country === "USA" || country === "US";
    return !!group &&
      maxColumnLengthAllowed.includes(columnsLength) &&
      columnAllowed.includes(column) &&
      !isNational
      ? internationalLabel
      : "";
  };

  handleAnyButton = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.onSelectAllRunnerEntries(
      get(this.props, "bettingInterest.biNumber").toString()
    );
  };

  handleSelectionButtonClick = (
    column: number,
    event?: SyntheticKeyboardEvent<*>
  ) => {
    return this.props.redirectToRaceCard && this.props.race
      ? this.props.onRunnerSelection(
          get(this.props, "race"),
          get(this.props, "bettingInterest.biNumber"),
          get(this.props, "raceUrl"),
          event
        )
      : this.props.onRunnerSelect(
          get(this.props, "bettingInterest.biNumber").toString(),
          get(this.props, "leg", 0) > 0 ? get(this.props, "leg", 0) : column,
          this.props.isNumberFireSection
        );
  };

  fetchOdds = (): string => {
    return get(this.props, "bettingInterest.odds") !== undefined
      ? "odds"
      : "currentOdds";
  };

  renderAnyButton = (isAllSelected: boolean) => {
    let container = this.props.selectAllButtonLabel;

    if (this.props.group === "winGroup") {
      container = (
        <GroupWrapper>
          <span>Across</span>
          <span>the board</span>
        </GroupWrapper>
      );
    }

    return (
      <AnyButton
        key="selection-any"
        qaLabel="selection-any"
        isChecked={isAllSelected}
        onClick={this.handleAnyButton}
      >
        {container}
      </AnyButton>
    );
  };

  renderSelectionButtons = () => {
    const selectionType = this.props.numColumns === 1 ? "default" : "exotic";
    const isAllSelected =
      selectionType === "exotic"
        ? this.props.selections.every((selection) => !!selection)
        : false;

    if (this.props.wagerTypeCode === undefined) return null;

    const columns = times(this.props.numColumns, (column) => {
      if (
        this.props.wagerTypeCode === WagerTypeCodesEnum.WIN_SHOW &&
        column === 1
      )
        return <React.Fragment />;

      return !this.props.redirectToRaceCard ? (
        <Fragment key={`selection-${column + 1}`}>
          <SelectionButton
            selectionType={selectionType}
            number={column + 1}
            isChecked={!!this.props.selections[column]}
            isScratched={get(this.props, "runner.scratched")}
            onChange={() => this.handleSelectionButtonClick(column)}
            hasHover={this.props.hasHover}
            isAllSelected={isAllSelected}
            isFavorite={get(this.props, "bettingInterest.isFavorite")}
            currentOdd={formatOdd(
              get(this.props, "bettingInterest.currentOdds.numerator", 0),
              get(this.props, "bettingInterest.currentOdds.denominator", 0)
            )}
            trending={get(this.props, "bettingInterest.recentOdds[0].trending")}
            group={this.props.group}
            internationalLabel={this.getInternationalLabel(
              column + 1,
              this.props.numColumns
            )}
            wagerTypeCode={this.props.wagerTypeCode}
          />
        </Fragment>
      ) : (
        <Link
          key={`biNumberLink${get(this.props, "bettingInterest.biNumber")}`}
          to={`${this.props.raceUrl}&selectedRunner=${get(
            this.props,
            "bettingInterest.biNumber"
          )}`}
          onClick={(event: SyntheticKeyboardEvent<*>) => {
            this.handleSelectionButtonClick(column, event);
          }}
          data-qa-label={`biNumberLink-${get(
            this.props,
            "bettingInterest.biNumber"
          )}`}
        >
          <SelectionButton
            key={`selection-${column + 1}`}
            selectionType="default"
            redirectToRacecard
            number={column + 1}
            isChecked={!!this.props.selections[column]}
            isScratched={get(this.props, "runner.scratched")}
            hasHover={this.props.hasHover}
            currentOdd={formatOdd(
              get(this.props, "bettingInterest.currentOdds.numerator", 0),
              get(this.props, "bettingInterest.currentOdds.denominator", 0)
            )}
          />
        </Link>
      );
    });

    return (
      <Fragment>
        <RunnerSelectionWrapped
          isBorderActive={selectionType !== "default"}
          data-qa-label="runnerLine-selectionButtons"
          fullWidth={this.props.fullWidth}
        >
          {columns}
          {selectionType === "exotic" && this.renderAnyButton(isAllSelected)}
        </RunnerSelectionWrapped>
      </Fragment>
    );
  };

  renderTrendingIcon = (trending: boolean) =>
    trending ? (
      <Icon icon={caretUp} color={buildColor("green", "500")} />
    ) : (
      <Icon icon={caretDown} color={buildColor("red", "500")} />
    );

  renderOdds = () => (
    <Fragment>
      <OddsContainer>
        <OddInfo
          hasTrending={
            get(this.props, "bettingInterest.recentOdds[0].trending", null) !==
            null
          }
        >
          {get(this.props, "bettingInterest.isFavorite") && (
            <Tag size={16} name="FAV" tagType="Fav" />
          )}
          {get(this.props, "bettingInterest.recentOdds[0].trending", null) !==
          null
            ? this.renderTrendingIcon(
                get(this.props, "bettingInterest.recentOdds[0].trending")
              )
            : null}
          {get(this.props, "bettingInterest.currentOdds", null) !== null ||
          get(this.props, "bettingInterest.odds", null) !== null ? (
            formatOdd(
              get(
                this.props,
                `bettingInterest.${this.fetchOdds()}.numerator`,
                0
              ),
              get(
                this.props,
                `bettingInterest.${this.fetchOdds()}.denominator`,
                0
              )
            )
          ) : (
            <Tag size={16} tagType="CoupledTag" name="N/A" />
          )}
        </OddInfo>
      </OddsContainer>
    </Fragment>
  );

  renderBetSection = (isScratched: boolean, selectionType: string) => {
    if (!isScratched) {
      return selectionType === "default" && this.props.isWagerable
        ? this.renderSelectionButtons()
        : this.renderOdds();
    }

    return <ScratchedTag tagType="CoupledTag" name="scratched" size={17} />;
  };

  renderLine = () => {
    const hasTimeformInfo =
      this.props.showSilks && !!get(this.props, "runner.timeform");
    const flags = get(this.props, "runner.timeform.flags", {});
    const tags = Object.entries(flags).reduce((acc, [flag, enabled]) => {
      if (enabled === true) {
        acc.push({ tag: flag, text: startCase(flag) });
      }
      return acc;
    }, []);
    const selectionType = this.props.numColumns === 1 ? "default" : "exotic";
    const isScratched = get(this.props, "runner.scratched");
    const freePick = get(
      this.props,
      `runner.${this.props.freePickSource}.freePick`,
      {}
    );
    const freePickNumber = get(freePick, "number");
    const freePickInfo = get(freePick, "info");

    const renderOnboarding = () => (
      <HorseNameOnboardingTooltip onClick={this.props.onTooltipClick}>
        Tap for more
      </HorseNameOnboardingTooltip>
    );

    const handleDefaultSilk = () => {
      return this.props.showSilks ? <Silk /> : <React.Fragment />;
    };

    const runnerId = get(this.props, "runner.runnerId");
    return (
      <RunnerLineWrapper
        className="hasBottomBorder"
        isHighlighted={this.props.isHighlighted}
        hasMarginLeft={this.props.hasMarginLeft}
        hasDashedBorder={this.props.hasDashedBorder}
        isOpen={this.props.isOpen && !this.props.isNumberFireSection}
        hasBiggerBottomBorder={this.props.selections.length > 1}
        hasTimeformInfo={hasTimeformInfo}
      >
        <RunnerContainer>
          <RunnerWrapper
            key={`runner-${runnerId}`}
            data-qa-label={`${runnerId}-runnerLine${
              `${get(this.props, "runner.runnerId", "0")}`.match(/[a-zA-Z]/i)
                ? "-coupled"
                : ""
            }${isScratched ? "-scratched" : ""}`}
            scratched={isScratched}
          >
            <RunnerInfo
              isNumberFireSection={
                !!freePickNumber && !this.props.isNumberFireSection
              }
              numberFire={!!freePickNumber}
              addMarginBottom={selectionType !== "default"}
            >
              <RunnerJockeyWrapper>
                <JockeySilkContainer hasTimeformInfo={hasTimeformInfo}>
                  {hasTimeformInfo && !this.state.hasSilksError ? (
                    <JockeySilk
                      isSilkLoaded={this.state.isSilkLoaded}
                      src={get(this.props, "runner.timeform.silkUrlSvg", "")}
                      alt={`Silk image for runner number ${runnerId}`}
                      onError={() => {
                        if (typeof this.props.handleError === "function") {
                          this.props.handleError(runnerId);
                        }
                        this.setState({ hasSilksError: true });
                      }}
                      onLoad={() => {
                        this.setState({ isSilkLoaded: true });
                      }}
                    />
                  ) : (
                    handleDefaultSilk()
                  )}
                  <RunnerNumber
                    raceTypeCode={this.props.raceTypeCode}
                    runnerId={runnerId}
                    hasTimeformInfo={hasTimeformInfo}
                    isBigSize={!hasTimeformInfo}
                  />
                </JockeySilkContainer>
                <RunnerInfoContainer
                  isNumberFireSection={this.props.isNumberFireSection}
                >
                  {this.props.isNumberFireSection && (
                    <NumberFirePick>
                      {`${formatSequencial(freePickNumber)} Pick`}
                    </NumberFirePick>
                  )}
                  <HorseDescription
                    isNumberFireSection={this.props.isNumberFireSection}
                  >
                    <HorseName>
                      {get(this.props, "runner.horseName")}
                      {!this.props.isNumberFireSection &&
                        !this.props.isOpen &&
                        this.props.shouldShowFirstOnboarding &&
                        this.props.index === 0 &&
                        renderOnboarding()}
                    </HorseName>
                    {!get(this.props, "runner.scratched") &&
                      !this.props.isNumberFireSection &&
                      this.props.isExpandable && (
                        <ExpandIconWrapper>
                          <Icon
                            icon={!this.props.isOpen ? arrowDown : arrowUp}
                            color={buildColor("blue", "500")}
                          />
                        </ExpandIconWrapper>
                      )}
                    {this.props.isCoupled && (
                      <CoupledTagContainer>
                        <Tag
                          onClick={(e: Event) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return this.props.openHelpModal("Coupled Runners");
                          }}
                          tagType="CoupledTag"
                          name="COUPLED"
                          size={16}
                        />
                      </CoupledTagContainer>
                    )}
                  </HorseDescription>
                  {this.props.isNumberFireSection && (
                    <NumberFireComment>{freePickInfo}</NumberFireComment>
                  )}
                  {!this.props.isNumberFireSection && (
                    <DriverDescription>
                      <span>
                        {this.props.raceTypeCode === "H" ? "D" : "J"}:{" "}
                        {get(this.props, "runner.jockey")}
                      </span>
                      <span> T: {get(this.props, "runner.trainer")}</span>
                    </DriverDescription>
                  )}
                </RunnerInfoContainer>
              </RunnerJockeyWrapper>
              <BetSection
                isScratched={isScratched}
                isNumberFireSection={this.props.isNumberFireSection}
              >
                {this.renderBetSection(isScratched, selectionType)}
                {this.props.showRunnerUpdate && !isScratched ? (
                  <USOdds>
                    <USOddsText>US ODDS:</USOddsText>
                    <USOddsContainer>
                      {USformatOdd(
                        get(
                          this.props,
                          "bettingInterest.currentOdds.numerator",
                          0
                        ),
                        get(
                          this.props,
                          "bettingInterest.currentOdds.denominator",
                          0
                        )
                      )}
                    </USOddsContainer>
                  </USOdds>
                ) : (
                  ""
                )}
              </BetSection>
            </RunnerInfo>
            {!isScratched &&
              !this.props.isNumberFireSection &&
              this.props.enableNewRacePage && (
                <RunnerTags
                  hasTimeformInfo={hasTimeformInfo}
                  numberFire={freePickNumber}
                  tags={tags}
                  isOpen={this.props.isOpen}
                  onRaceTagsInfoClick={() => {
                    const onRaceClick = get(this.props, "onRaceTagsInfoClick");
                    if (onRaceClick)
                      onRaceClick({
                        isOpen: true,
                        linkedRunner: {
                          raceTypeCode: this.props.raceTypeCode,
                          runnerId: this.props.runner?.runnerId || "",
                          horseName: this.props.runner?.horseName || ""
                        },
                        currentRunnerTags: tags
                          ? tags.map((tag) => tag.tag)
                          : [],
                        concatRunnerTags:
                          tags.map((tag) => tag.tag).join(", ") || "",
                        trackName: get(
                          this.props,
                          "currentRace.track.trackName",
                          ""
                        ),
                        raceNumber: get(
                          this.props,
                          "currentRace.raceNumber",
                          ""
                        )
                      });
                  }}
                />
              )}
            {!!freePickNumber &&
              !this.props.isNumberFireSection &&
              !this.props.enableNewRacePage && (
                <NumberFire
                  hasTimeformInfo={hasTimeformInfo}
                  isRAS={this.props.isRAS}
                >
                  {this.props.isRAS ? (
                    <RacingAndSportsIcon />
                  ) : (
                    !this.props.isSRW && (
                      <Icon
                        icon={numberFire}
                        color={buildColor("grey", "800")}
                      />
                    )
                  )}
                  &nbsp;{`${formatSequencial(freePickNumber)} Pick`}
                </NumberFire>
              )}
          </RunnerWrapper>
        </RunnerContainer>
        {selectionType === "exotic" && !isScratched && (
          <ExoticBetting fullWidth={this.props.fullWidth}>
            {this.renderSelectionButtons()}
          </ExoticBetting>
        )}
      </RunnerLineWrapper>
    );
  };

  render() {
    const isScratched = get(this.props, "runner.scratched");
    return (
      <ExpandableRow
        isExpandable={!isScratched}
        header={this.renderLine()}
        renderIcon={false}
        onExpand={this.onExpand}
        isOpen={!this.props.isNumberFireSection && this.props.isOpen}
      >
        {this.props.isOpen &&
          this.props.expandedInfo(
            get(this.props, "runner.runnerId"),
            this.props.leg,
            this.props.isNumberFireSection
          )}
      </ExpandableRow>
    );
  }
}
